/**
 * PreviewStep — final review screen of the ProfileComplete v2.0 wizard.
 *
 * Industry-standard review screen (Stripe Atlas / Razorpay / Plaid):
 *   - Same SectionPanel pattern as the other wizard steps (consistency)
 *   - Each section has an "Edit" affordance to jump back to that step
 *   - Definition-list layout: small uppercase label + bold value below
 *   - Documents render as a status list (✓ uploaded · ◯ pending)
 *
 * Wallet recharge moved out of this step into a dedicated optional
 * step (CreditsStep) so the review page stays focused on confirming
 * what's about to be saved.
 *
 * Props:
 *   - basicInfo, userType, establishment, uploadedDocuments, requiredDocuments
 *   - onEditStep  — (i) => void, called when the section's "Edit" button is clicked
 */
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { Tag } from 'primereact/tag';
import { classNames } from 'primereact/utils';

/* ── Lookup tables ───────────────────────────────────────────────────── */
const USER_TYPE_LABEL = {
    loan_agent: 'Loan Agent',
    dsa: 'DSA',
    financial_consultant: 'Financial Consultant',
    insurance_agent: 'Insurance Agent',
    investment_advisor: 'Investment Advisor',
    tax_consultant: 'Tax Consultant',
    real_estate_agent: 'Real Estate Agent',
    other: 'Other',
};

const LOAN_PRODUCT_LABEL = {
    home_loan: 'Home Loan',
    personal_loan: 'Personal Loan',
    business_loan: 'Business Loan',
    vehicle_loan: 'Vehicle Loan',
    education_loan: 'Education Loan',
    gold_loan: 'Gold Loan',
    credit_card: 'Credit Card',
};

const DISBURSEMENT_LABEL = {
    less_than_1_lakh: 'Less than ₹1 Lakh',
    '1_lakh_to_10_lakh': '₹1 Lakh – ₹10 Lakh',
    '10_lakh_to_50_lakh': '₹10 Lakh – ₹50 Lakh',
    '50_lakh_to_1_crore': '₹50 Lakh – ₹1 Crore',
    '1_to_5_crore': '₹1 – 5 Crore',
    more_than_5_crore: 'More than ₹5 Crore',
};

const ESTABLISHMENT_LABEL = {
    individual: 'Individual',
    proprietorship: 'Proprietorship',
    partnership: 'Partnership',
    llp: 'LLP',
    private_limited: 'Private Limited',
    public_limited: 'Public Limited',
    one_man_company: 'One Person Company',
    society: 'Society',
    trust: 'Trust',
    nbfc: 'NBFC',
    bank: 'Bank',
    mfi: 'MFI',
};

const dash = '—';
const formatList = (arr, dict) => {
    if (!Array.isArray(arr) || arr.length === 0) return dash;
    return arr.map((v) => dict[v] || v).join(', ');
};

const PreviewStep = ({
    basicInfo,
    userType,
    establishment,
    uploadedDocuments = {},
    requiredDocuments = [],
    onEditStep,
}) => {
    const mobileFormatted = basicInfo.mobile_number ? `+91 ${basicInfo.mobile_number}` : dash;

    const mandatoryDocs = requiredDocuments.filter((d) => d.is_mandatory);
    const uploadedMandatory = mandatoryDocs.filter((d) => uploadedDocuments[d.name]);
    const docComplete = mandatoryDocs.length === 0
        || uploadedMandatory.length === mandatoryDocs.length;

    return (
        <div>
            {/* ─ Personal details ─ mirrors the form: 4 fields in a single
                 desktop row (col-3 each) + 2 per row on mobile (col-6 each) */}
            <SectionPanel
                icon="pi-user"
                title="Personal details"
                onEdit={onEditStep ? () => onEditStep(0) : undefined}
            >
                <DefinitionList>
                    <Definition label="First name" value={basicInfo.first_name || dash} cols="col-6 md:col-3" />
                    <Definition label="Last name" value={basicInfo.last_name || dash} cols="col-6 md:col-3" />
                    <Definition label="Mobile" value={mobileFormatted} cols="col-6 md:col-3" />
                    <Definition label="Email" value={basicInfo.email || dash} cols="col-6 md:col-3" />
                </DefinitionList>
            </SectionPanel>

            {/* ─ Address ─ mirrors the form: 3 fields in a single desktop row
                 (col-4 each) + 2 per row on mobile + full-width address */}
            <SectionPanel
                icon="pi-map-marker"
                title="Address"
                onEdit={onEditStep ? () => onEditStep(0) : undefined}
            >
                <DefinitionList>
                    <Definition label="Pincode" value={basicInfo.pincode || dash} cols="col-6 md:col-4" />
                    <Definition label="City" value={basicInfo.city || dash} cols="col-6 md:col-4" />
                    <Definition label="State" value={basicInfo.state || dash} cols="col-6 md:col-4" />
                    {basicInfo.address && (
                        <Definition label="Street address" value={basicInfo.address} fullWidth />
                    )}
                </DefinitionList>
            </SectionPanel>

            {/* ─ Role & focus ───────────────────────────────────────── */}
            <SectionPanel
                icon="pi-id-card"
                title="Role & focus"
                onEdit={onEditStep ? () => onEditStep(1) : undefined}
            >
                <DefinitionList>
                    <Definition label="Defined as" fullWidth>
                        {userType.user_type_selections?.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                                {userType.user_type_selections.map((v) => (
                                    <Tag
                                        key={v}
                                        value={USER_TYPE_LABEL[v] || v}
                                        severity="info"
                                        className="text-xs"
                                        style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }}
                                    />
                                ))}
                            </div>
                        ) : <span className="text-500">{dash}</span>}
                    </Definition>
                    <Definition
                        label="Loan products handled"
                        value={formatList(userType.loan_products_handled, LOAN_PRODUCT_LABEL)}
                    />
                    <Definition
                        label="Referral products"
                        value={formatList(userType.referral_loan_products, LOAN_PRODUCT_LABEL)}
                    />
                    <Definition
                        label="Monthly disbursements"
                        value={DISBURSEMENT_LABEL[userType.total_loan_disbursements_per_month] || dash}
                    />
                    <Definition
                        label="Active areas"
                        value={Array.isArray(userType.active_cities) && userType.active_cities.length > 0
                            ? `${userType.active_cities.length} ${userType.active_cities.length === 1 ? 'area' : 'areas'}`
                            : dash}
                    />
                    <Definition
                        label="Support areas"
                        value={Array.isArray(userType.support_needed_cities) && userType.support_needed_cities.length > 0
                            ? `${userType.support_needed_cities.length} ${userType.support_needed_cities.length === 1 ? 'area' : 'areas'}`
                            : dash}
                    />
                </DefinitionList>
            </SectionPanel>

            {/* ─ Establishment & banking ────────────────────────────── */}
            <SectionPanel
                icon="pi-building"
                title="Establishment & banking"
                onEdit={onEditStep ? () => onEditStep(2) : undefined}
            >
                <DefinitionList>
                    <Definition
                        label="Establishment type"
                        value={ESTABLISHMENT_LABEL[establishment?.type_of_establishment] || dash}
                    />
                    {establishment?.designation && (
                        <Definition label="Designation" value={establishment.designation} />
                    )}
                    {establishment?.has_gst_certificate && establishment?.gst_certificate_number && (
                        <Definition label="GSTIN" value={establishment.gst_certificate_number} />
                    )}
                    <Definition
                        label="Bank name"
                        value={establishment?.bank_name || dash}
                    />
                    <Definition
                        label="Account number"
                        value={
                            establishment?.account_number
                                ? `•••• ${String(establishment.account_number).slice(-4)}`
                                : dash
                        }
                    />
                    <Definition label="IFSC" value={establishment?.ifsc_code || dash} />
                </DefinitionList>
            </SectionPanel>

            {/* ─ Documents ──────────────────────────────────────────── */}
            <SectionPanel
                icon="pi-file"
                title="Documents"
                titleAction={(
                    <Tag
                        value={
                            mandatoryDocs.length === 0
                                ? 'None required'
                                : docComplete
                                    ? 'All uploaded'
                                    : `${uploadedMandatory.length} / ${mandatoryDocs.length}`
                        }
                        severity={docComplete ? 'success' : 'warning'}
                        className="text-xs"
                    />
                )}
                onEdit={onEditStep ? () => onEditStep(3) : undefined}
            >
                {mandatoryDocs.length === 0 ? (
                    <div className="text-500 text-xs">No documents required for the selected establishment type.</div>
                ) : (
                    <ul className="list-none p-0 m-0">
                        {mandatoryDocs.map((d, idx) => {
                            const up = uploadedDocuments[d.name];
                            return (
                                <li
                                    key={d.name}
                                    className={classNames(
                                        'flex align-items-center gap-2 py-2',
                                        idx > 0 ? 'border-top-1 surface-border' : ''
                                    )}
                                >
                                    <i
                                        className={up
                                            ? 'pi pi-check-circle text-green-500'
                                            : 'pi pi-circle text-400'}
                                        style={{ fontSize: '0.85rem' }}
                                        aria-hidden="true"
                                    />
                                    <span className="text-900 text-xs font-medium flex-1 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                        {d.label || d.name}
                                    </span>
                                    <span
                                        className={classNames(
                                            'text-xs white-space-nowrap overflow-hidden text-overflow-ellipsis',
                                            up ? 'text-700' : 'text-500'
                                        )}
                                        style={{ maxWidth: '60%' }}
                                    >
                                        {up ? up.file_name : 'Not uploaded'}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </SectionPanel>

        </div>
    );
};

/* ════════════════════════════════════════════════════════════════════
 * SectionPanel — PrimeReact Panel with a slim custom header. Matches
 * BasicInfoStep / UserTypeStep / EstablishmentStep / DocumentsStep, plus
 * an optional Edit button on the right (for jumping back to a step).
 * The pt.title style ensures the title slot grows to push Edit to the
 * right edge of the header.
 * ════════════════════════════════════════════════════════════════════ */
const SectionPanel = ({ icon, title, subtitle, titleAction, onEdit, children, className = '' }) => {
    const headerContent = (
        <div className="flex align-items-center gap-2 w-full">
            {icon && (
                <div
                    className="flex align-items-center justify-content-center border-round flex-shrink-0"
                    style={{
                        width: '1.75rem',
                        height: '1.75rem',
                        backgroundColor: 'var(--primary-50)',
                        color: 'var(--primary-color)',
                        fontSize: '0.85rem',
                    }}
                >
                    <i className={`pi ${icon}`} aria-hidden="true" />
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="flex align-items-center gap-2 flex-wrap">
                    <span className="text-900 font-semibold text-sm">{title}</span>
                    {titleAction}
                </div>
                {subtitle && (
                    <div className="text-500 font-normal" style={{ fontSize: '0.7rem', marginTop: '0.1rem' }}>
                        {subtitle}
                    </div>
                )}
            </div>
            {onEdit && (
                <Button
                    label="Edit"
                    icon="pi pi-pencil"
                    text
                    size="small"
                    severity="secondary"
                    onClick={onEdit}
                    aria-label={`Edit ${title}`}
                    pt={{
                        root: { style: { padding: '0.25rem 0.5rem', fontSize: '0.75rem' } },
                        label: { className: 'hidden md:inline ml-1' },
                    }}
                />
            )}
        </div>
    );

    return (
        <Panel
            header={headerContent}
            toggleable={false}
            className={classNames('mb-3', className)}
            pt={{
                header: { className: 'py-2 px-3' },
                title: { style: { flex: 1 } },
                content: { className: 'p-3' },
            }}
        >
            {children}
        </Panel>
    );
};

/* ── DefinitionList — 2-column key/value pairs (1 on mobile) ───────── */
const DefinitionList = ({ children }) => (
    <div className="grid">{children}</div>
);

const Definition = ({ label, value, children, fullWidth, cols }) => {
    const colsClass = fullWidth
        ? 'col-12'
        : cols || 'col-12 sm:col-6';
    return (
        <div className={classNames('mb-2', colsClass)}>
            <div
                className="text-500 text-xs font-medium mb-1"
                style={{ letterSpacing: '0.04em' }}
            >
                {label}
            </div>
            {children !== undefined ? children : (
                <div className="text-900 text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                    {value || dash}
                </div>
            )}
        </div>
    );
};

PreviewStep.runValidations = () => ({});
PreviewStep.hasErrors = () => false;

export default PreviewStep;
